// Revised 2024 Cunning Strike (Rogue 5), shared by the attack prompt and the server route that resolves each effect.
// Improved Cunning Strike (Rogue 11): up to two different effects on one Sneak Attack, each paying its die cost.
// Devious Strikes (Rogue 14): Daze, Knock Out and Obscure join the options.
export const CUNNING_STRIKE_FEATURE_ID = 'rogue:cunning-strike';
export const IMPROVED_CUNNING_STRIKE_LEVEL = 11;

export const CUNNING_STRIKE_EFFECTS = Object.freeze({
  poison: Object.freeze({ id: 'poison', label: 'Poison', dieCost: 1, levelRequired: 5, saveAbility: 'constitution' }),
  trip: Object.freeze({ id: 'trip', label: 'Trip', dieCost: 1, levelRequired: 5, saveAbility: 'dexterity' }),
  withdraw: Object.freeze({ id: 'withdraw', label: 'Withdraw', dieCost: 1, levelRequired: 5 }),
  daze: Object.freeze({ id: 'daze', label: 'Daze', dieCost: 2, levelRequired: 14, saveAbility: 'constitution' }),
  'knock-out': Object.freeze({ id: 'knock-out', label: 'Knock Out', dieCost: 6, levelRequired: 14, saveAbility: 'constitution' }),
  obscure: Object.freeze({ id: 'obscure', label: 'Obscure', dieCost: 3, levelRequired: 14, saveAbility: 'dexterity' }),
  // Supreme Sneak (Thief 9): the attack doesn't end the Hide action's Invisible if the Thief ends the turn behind
  // Three-Quarters or Total Cover.
  'stealth-attack': Object.freeze({ id: 'stealth-attack', label: 'Stealth Attack', dieCost: 1, levelRequired: 9, requires: 'supremeSneak' }),
});
// What a failed save means, for prompts and notices.
export const CUNNING_STRIKE_FAILURE_TEXT = Object.freeze({ poison: 'Poisoned', trip: 'Prone', daze: 'Dazed', 'knock-out': 'Unconscious', obscure: 'Blinded' });

/** Sneak Attack dice: one d6 at level 1 and at every odd Rogue level. */
export const sneakAttackDiceCount = (rogueLevel) => {
  const level = Math.floor(Number(rogueLevel));
  return Number.isFinite(level) && level > 0 ? Math.ceil(level / 2) : 0;
};
/** How many different effects one Sneak Attack can carry. */
export const cunningStrikeMaxEffects = (rogueLevel) => (Number(rogueLevel) >= IMPROVED_CUNNING_STRIKE_LEVEL ? 2 : Number(rogueLevel) >= 5 ? 1 : 0);
const entitled = (effect, rogueLevel, features = {}) => Boolean(effect) && Number(rogueLevel) >= effect.levelRequired && (!effect.requires || features[effect.requires] === true);
/** The effects a Rogue knows; `features.supremeSneak` adds the Thief's Stealth Attack. */
export const cunningStrikeEffectsForLevel = (rogueLevel, features = {}) => Object.values(CUNNING_STRIKE_EFFECTS).filter((effect) => entitled(effect, rogueLevel, features));

/**
 * Why a set of effect ids can't go on one Sneak Attack at this Rogue level, or null when it can: every effect known and
 * learned, no repeats, no more than the level allows, and their dice within the Sneak Attack dice.
 */
export const cunningStrikeSelectionProblem = (rogueLevel, effectIds = [], features = {}) => {
  const effects = effectIds.map((id) => CUNNING_STRIKE_EFFECTS[id]);
  if (effects.some((effect) => !entitled(effect, rogueLevel, features))) return 'cunning-strike-not-entitled';
  if (new Set(effectIds).size !== effectIds.length) return 'duplicate-effect';
  if (effectIds.length > cunningStrikeMaxEffects(rogueLevel)) return 'maximum-effects-exceeded';
  if (effects.reduce((sum, effect) => sum + effect.dieCost, 0) > sneakAttackDiceCount(rogueLevel)) return 'insufficient-sneak-attack-dice';
  return null;
};
